import React, { useState } from "react";
import { ArrowRight, ArrowLeft, Check, RotateCcw } from "lucide-react";

// Interactive "Build your plan" quiz → recommends a tier + coach, then drops into signup.
const QUESTIONS = [
  {
    key: "goal",
    q: "What's your #1 goal right now?",
    options: [
      { v: "move", label: "Move more & build a habit" },
      { v: "strength", label: "Get stronger / build muscle" },
      { v: "nutrition", label: "Eat better & lose weight" },
      { v: "balance", label: "Less stress, better sleep & recovery" },
    ],
  },
  {
    key: "who",
    q: "Who's coming on this journey?",
    options: [
      { v: "solo", label: "Just me" },
      { v: "partner", label: "Me + my partner" },
      { v: "family", label: "My whole family" },
    ],
  },
  {
    key: "style",
    q: "What kind of coach do you want in your corner?",
    options: [
      { v: "amara", label: "Warm & encouraging" },
      { v: "jabari", label: "Tough love, no excuses" },
      { v: "zuri", label: "Calm, wellness & recovery" },
      { v: "kofi", label: "Data-driven & strategic" },
    ],
  },
];

const COACH_NAMES = { amara: "Amara", jabari: "Jabari", zuri: "Zuri", kofi: "Kofi" };

function recommend(ans) {
  let tier = "Individual";
  if (ans.who === "family") tier = "Family";
  else if (ans.who === "partner") tier = "Family";
  else if (ans.goal === "balance") tier = "Premium Elite";
  const coach = COACH_NAMES[ans.style] || "Amara";
  const reasonMap = {
    move: "a daily-win habit engine with streaks & gentle nudges",
    strength: "training plans + progress tracking that adapt to you",
    nutrition: "effortless meal tracking with human, guilt-free guidance",
    balance: "recovery, sleep & mind tools plus 1:1 human check-ins",
  };
  return { tier, coach, reason: reasonMap[ans.goal] || "your complete wellness home" };
}

export function PlanQuiz({ onJoin }) {
  const [step, setStep] = useState(0);
  const [ans, setAns] = useState({});
  const done = step >= QUESTIONS.length;
  const rec = done ? recommend(ans) : null;

  const pick = (key, v) => {
    setAns((a) => ({ ...a, [key]: v }));
    setStep((s) => s + 1);
  };
  const reset = () => { setAns({}); setStep(0); };

  return (
    <section id="build-plan" className="relative mx-auto max-w-3xl px-5 py-20 lg:px-8" data-testid="plan-quiz">
      <div className="mb-10 text-center">
        <p className="eyebrow">30 seconds</p>
        <h2 className="mt-5 text-3xl font-semibold text-mist sm:text-4xl">Build your plan</h2>
        <p className="mx-auto mt-4 max-w-lg text-mist-muted">Answer three quick questions and we'll match you to the right plan and coach.</p>
      </div>

      <div className="glass relative overflow-hidden rounded-[2rem] p-6 sm:p-10">
        <div className="pointer-events-none absolute -left-16 -bottom-16 h-56 w-56 rounded-full bg-sage/15 blur-[90px]" />

        {/* Progress */}
        {!done && (
          <div className="relative mb-8 flex items-center gap-2" data-testid="plan-quiz-progress">
            {QUESTIONS.map((_, i) => (
              <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-brand" : "bg-white/10"}`} />
            ))}
          </div>
        )}

        {!done ? (
          <div className="relative">
            <div className="text-xs uppercase tracking-widest text-brand-soft">Question {step + 1} of {QUESTIONS.length}</div>
            <h3 className="mt-2 text-2xl font-semibold text-mist" data-testid="plan-quiz-question">{QUESTIONS[step].q}</h3>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {QUESTIONS[step].options.map((o) => (
                <button key={o.v} onClick={() => pick(QUESTIONS[step].key, o.v)} data-testid={`plan-quiz-option-${o.v}`}
                  className="group flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4 text-left text-sm text-mist transition-all hover:-translate-y-0.5 hover:border-brand/40 hover:bg-brand/[0.06]">
                  {o.label}
                  <ArrowRight size={16} className="text-mist-muted transition-transform group-hover:translate-x-1 group-hover:text-brand" />
                </button>
              ))}
            </div>
            {step > 0 && (
              <button onClick={() => setStep((s) => s - 1)} data-testid="plan-quiz-back" className="mt-6 inline-flex items-center gap-1.5 text-sm text-mist-muted hover:text-mist">
                <ArrowLeft size={15} /> Back
              </button>
            )}
          </div>
        ) : (
          <div className="relative text-center" data-testid="plan-quiz-result">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand/15 text-brand"><Check size={28} /></div>
            <h3 className="mt-5 text-2xl font-semibold text-mist">Your match: {rec.tier}</h3>
            <p className="mx-auto mt-3 max-w-md text-mist-muted">
              With <span className="font-semibold text-mist">Coach {rec.coach}</span> in your corner — {rec.reason}.
            </p>
            <button onClick={onJoin} data-testid="plan-quiz-cta" className="btn-primary mx-auto mt-8 inline-flex items-center gap-2 text-base">
              Start my 30-day free trial <ArrowRight size={17} />
            </button>
            <button onClick={reset} data-testid="plan-quiz-reset" className="mt-4 inline-flex items-center gap-1.5 text-sm text-mist-muted hover:text-mist">
              <RotateCcw size={14} /> Retake
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
